import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Icon,
  Paper,
  Card,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';

// Helper to filter object keys based on an allowed list
const filterObjectByKeys = (raw, allowed) => {
  if (!raw || !allowed || !Array.isArray(allowed)) return {};
  return Object.keys(raw)
    .filter((key) => allowed.includes(key))
    .reduce((obj, key) => {
      obj[key] = raw[key];
      return obj;
    }, {});
};

const DiscountTiersTable = ({ tiers }) => {
  if (!tiers || tiers.length === 0) return null;
  return (
    <ArgonBox mt={2}>
      <ArgonTypography variant="subtitle2" fontWeight="bold" mb={1}>
        Discount Tiers
      </ArgonTypography>
      <TableContainer component={Paper} elevation={0} variant="outlined">
        <Table size="small">
          <TableHead sx={{ display: 'contents' }}>
            <TableRow>
              <TableCell>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Tier Name
                </ArgonTypography>
              </TableCell>
              <TableCell>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Lower Bound
                </ArgonTypography>
              </TableCell>
              <TableCell>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Upper Bound
                </ArgonTypography>
              </TableCell>
              <TableCell>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Discount
                </ArgonTypography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tiers.map((tier) => {
              const fields = tier.fields || {};
              const discountValue =
                fields.SBQQ__Discount__c != null
                  ? `${parseFloat(fields.SBQQ__Discount__c).toFixed(2)}%`
                  : fields.SBQQ__DiscountAmount__c != null
                  ? `$${parseFloat(fields.SBQQ__DiscountAmount__c).toFixed(2)}`
                  : 'N/A';
              return (
                <TableRow key={tier.id || tier.sfId}>
                  <TableCell>{tier.name}</TableCell>
                  <TableCell>{fields.SBQQ__LowerBound__c}</TableCell>
                  <TableCell>{fields.SBQQ__UpperBound__c || 'And up'}</TableCell>
                  <TableCell>{discountValue}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </ArgonBox>
  );
};

const CpqView = ({ product, basicFields, renderFieldsProp }) => {
  const [expandedItem, setExpandedItem] = useState({});
  const handleItemClick = (itemId) => setExpandedItem((prev) => ({ ...prev, [itemId]: !prev[itemId] }));

  console.log('[CPQVIEW]', product);

  // --- NEW: Grouping Logic using useMemo for performance ---
  const groupedSchedules = useMemo(() => {
    const scheduleApiResponses =
      [product?.related_data?.discountSchedules?.find((ds) => ds?.type === 'products')] || [];
    if (scheduleApiResponses.length === 0) {
      return {};
    }

    const groups = {};

    scheduleApiResponses?.forEach((response) => {
      const type = response.type || 'unknown';
      const objects = response.objects || [];

      if (objects.length > 0) {
        // Use a more user-friendly title for the group
        const groupTitle =
          {
            products: 'Product Schedules',
            productOptions: 'Product Option Schedules',
            features: 'Feature Schedules',
          }[type] || 'Other Schedules';

        if (!groups[groupTitle]) {
          groups[groupTitle] = [];
        }
        groups[groupTitle].push(...objects);
      }
    });

    return groups;
  }, [product]);

  const groupTitles = Object.keys(groupedSchedules);

  if (groupTitles.length === 0) {
    return <ArgonTypography variant="body2">No CPQ Discount Schedules available for this product.</ArgonTypography>;
  }

  // --- RENDER THE GROUPED SECTIONS ---
  return groupTitles.map((title) => (
    <ArgonBox key={title} mb={4}>
      <Card sx={{ padding: '1rem' }}>
        <ArgonTypography variant="body2" sx={{ color: grey[800] }} fontWeight="bold" mb={2}>
          {title}
        </ArgonTypography>
        {groupedSchedules[title].map((owner) => {
          const schedule = owner.discountSchedules;
          if (!schedule) return null;

          const scheduleId = schedule.sfId || schedule.id;
          console.log('scheduleId', scheduleId);

          const filteredFields = filterObjectByKeys(schedule.fields, basicFields);

          // Get the name of the specific owner (e.g., the name of the Product Option)
          const ownerName = owner.IDFieldList?.Name || owner.IDFieldList?.SBQQ__OptionalSKU__r?.Name || '';

          return (
            <Card sx={{ marginBottom: '1rem' }} key={scheduleId}>
              <ArgonBox>
                <ArgonBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={() => handleItemClick(scheduleId)}
                  style={{ cursor: 'pointer' }}
                  borderRadius="md"
                  p={1.5}
                >
                  <ArgonBox>
                    <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
                      {schedule.name}
                    </ArgonTypography>
                    {/* Display the specific owner's name if it exists */}
                    {ownerName && (
                      <ArgonTypography variant="caption" color="textSecondary">
                        For: <strong>{ownerName}</strong>
                      </ArgonTypography>
                    )}
                  </ArgonBox>

                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemClick(scheduleId);
                    }}
                  >
                    <Icon>{expandedItem[scheduleId] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                  </IconButton>
                </ArgonBox>
                <Collapse in={expandedItem[scheduleId]} timeout="auto" unmountOnExit>
                  <ArgonBox p={2}>
                    {renderFieldsProp(filteredFields)}
                    <DiscountTiersTable tiers={schedule.discountTiers} />
                  </ArgonBox>
                </Collapse>
              </ArgonBox>
            </Card>
          );
        })}
      </Card>
    </ArgonBox>
  ));
};

const RcaTiersTable = ({ tiers, scheduleName }) => {
  const relevantTiers = tiers?.filter((tier) => tier.priceAdjustmentScheduleName === scheduleName) || [];
  if (relevantTiers.length === 0) return null;
  return (
    <ArgonBox mt={2}>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: grey[200], display: 'contents' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Lower Bound
                </ArgonTypography>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Tier Type
                </ArgonTypography>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Value
                </ArgonTypography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {relevantTiers.map((tier) => (
              <TableRow key={tier.recordId}>
                <TableCell>{tier.fields.LowerBound}</TableCell>
                <TableCell>{tier.fields.TierType}</TableCell>
                <TableCell>{tier.fields.TierValue}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ArgonBox>
  );
};

const RcaView = ({ schedules, tiers, basicFields, renderFieldsProp }) => {
  const [expandedItem, setExpandedItem] = useState({});
  const handleItemClick = (itemId) => setExpandedItem((prev) => ({ ...prev, [itemId]: !prev[itemId] }));

  if (!schedules || schedules.length === 0) {
    return <ArgonTypography variant="body2">No RCA Price Adjustment Schedules available.</ArgonTypography>;
  }

  return schedules.map((schedule) => {
    const filteredFields = Object.keys(schedule.fields || {})
      .filter((key) => basicFields.includes(key))
      .reduce((obj, key) => ({ ...obj, [key]: schedule.fields[key] }), {});

    return (
      <Card sx={{ marginBottom: '1rem' }} key={schedule.recordId}>
        <ArgonBox mb={0.5} mt={0.5}>
          <ArgonBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            onClick={() => handleItemClick(schedule.recordId)}
            style={{ cursor: 'pointer' }}
            p={1}
            borderRadius="md"
          >
            <ArgonTypography variant="h6" fontWeight="medium" color="secondary" ml={1}>
              {schedule.name}
            </ArgonTypography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleItemClick(schedule.recordId);
              }}
            >
              <Icon>{expandedItem[schedule.recordId] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
            </IconButton>
          </ArgonBox>
          <Collapse in={expandedItem[schedule.recordId]} timeout="auto" unmountOnExit>
            <ArgonBox p={2} sx={{ borderRadius: 'md' }}>
              {renderFieldsProp(filteredFields)}
              <RcaTiersTable tiers={tiers} scheduleName={schedule.name.replace(' (Sample)', '')} />
            </ArgonBox>
          </Collapse>
        </ArgonBox>
      </Card>
    );
  });
};

function PriceAdjustersTab({ product, org, renderFieldsProp, cpqBasicFields = [], rcaBasicFields = [] }) {
  const orgType = org?.type;

  const renderContent = () => {
    if (!product?.related_data) {
      return <ArgonTypography variant="body2">No data available for this product.</ArgonTypography>;
    }

    switch (orgType) {
      case 'CPQ':
        return <CpqView product={product} basicFields={cpqBasicFields} renderFieldsProp={renderFieldsProp} />;
      case 'RC':
        // Assuming RCA data structure is different and handled by RcaView
        return (
          <RcaView
            schedules={product.related_data?.priceAdjustmentSchedules}
            tiers={product.related_data?.priceAdjustmentTiers}
            basicFields={rcaBasicFields}
            renderFieldsProp={renderFieldsProp}
          />
        );
      default:
        return (
          <ArgonTypography variant="body2">No Price Adjusters available for this organization type.</ArgonTypography>
        );
    }
  };

  return <ArgonBox p={3}>{renderContent()}</ArgonBox>;
}

export default PriceAdjustersTab;
